import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Terms & Conditions | Trademark Organization Name</title>
        <meta name="description" content="Read our terms and conditions to understand the legal agreement between you and Trademark Organization Name for our trademark registration services." />
      </Helmet>

      {/* Header */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center space-x-2">
              <Shield className="w-8 h-8 text-secondary" />
              <span className="text-xl font-bold text-primary">Trademark Organization Name</span>
            </Link>
            <Link to="/">
              <Button className="btn-primary">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-primary mb-8">Terms & Conditions</h1>
          
          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Agreement to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms and Conditions constitute a legally binding agreement between you and Trademark Organization 
                Name regarding your use of our website and trademark registration services. By accessing our website or 
                engaging our services, you agree to be bound by these terms.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                If you do not agree with any part of these terms, you must not use our website or services. We reserve 
                the right to modify these terms at any time, and your continued use of our services constitutes 
                acceptance of any changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Services Provided</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Trademark Organization Name provides professional trademark registration services, including but not 
                limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Comprehensive trademark searches and availability analysis</li>
                <li>Trademark application preparation and filing with the USPTO</li>
                <li>Monitoring and responding to USPTO office actions</li>
                <li>Trademark portfolio management and maintenance</li>
                <li>Brand protection consultation and strategy</li>
                <li>Trademark monitoring and enforcement assistance</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Our services are provided for informational and professional assistance purposes. While we strive for 
                excellence, we do not guarantee trademark approval by the USPTO, as final decisions rest with the 
                examining attorney and applicable trademark law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Client Responsibilities</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                As a client of our services, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide accurate, complete, and truthful information for your trademark application</li>
                <li>Respond promptly to requests for information or clarification</li>
                <li>Review all documents and applications before submission to the USPTO</li>
                <li>Maintain the confidentiality of your account credentials</li>
                <li>Use your trademark in commerce as represented in your application</li>
                <li>Comply with all applicable laws and regulations regarding trademark use</li>
                <li>Pay all fees in a timely manner as agreed upon</li>
                <li>Notify us of any changes to your contact information or trademark use</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Fees and Payment Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                Our service fees are separate from government filing fees charged by the USPTO. All fees are clearly 
                disclosed before you engage our services. Payment is due upon agreement to proceed with services unless 
                otherwise arranged.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                USPTO filing fees are subject to change by the government and are non-refundable once filed. Our 
                service fees are earned upon completion of the specified work and are generally non-refundable, except 
                as specifically outlined in our refund policy for your selected service package.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Additional fees may apply for responding to office actions, filing amendments, or providing services 
                beyond the scope of your original package. We will notify you of any additional fees before performing 
                such work.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Intellectual Property Rights</h2>
              <p className="text-gray-700 leading-relaxed">
                All content on our website, including text, graphics, logos, images, and software, is the property of 
                Trademark Organization Name and is protected by copyright, trademark, and other intellectual property 
                laws. You may not reproduce, distribute, modify, or create derivative works without our express written 
                permission.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                The trademarks, logos, and service marks displayed on our website are owned by us or third parties. 
                Nothing on this website grants you any license or right to use any trademark without prior written 
                permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the fullest extent permitted by law, Trademark Organization Name shall not be liable for:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>USPTO rejection or refusal of your trademark application</li>
                <li>Delays in the trademark registration process caused by the USPTO</li>
                <li>Opposition proceedings initiated by third parties</li>
                <li>Loss of trademark rights due to non-use or abandonment</li>
                <li>Indirect, incidental, consequential, or punitive damages</li>
                <li>Lost profits, revenue, or business opportunities</li>
                <li>Data loss or system failures beyond our control</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Our total liability to you for any claims arising from our services shall not exceed the amount of fees 
                you paid to us for the specific services giving rise to the claim.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">No Guarantee of Registration</h2>
              <p className="text-gray-700 leading-relaxed">
                While we provide professional trademark services and use our expertise to maximize approval chances, 
                we cannot guarantee that the USPTO will approve your trademark application. Final registration decisions 
                are made by USPTO examining attorneys based on federal trademark law, existing registrations, and other 
                legal considerations beyond our control.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                We will make reasonable efforts to respond to office actions and overcome refusals, but some marks may 
                be inherently unregistrable or face insurmountable legal obstacles. In such cases, our service fees 
                remain earned for work performed.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Attorney-Client Relationship</h2>
              <p className="text-gray-700 leading-relaxed">
                If our services include legal representation by licensed attorneys, an attorney-client relationship 
                exists only for the specific scope of services agreed upon. This relationship does not extend to other 
                legal matters unless specifically engaged for such purposes.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Communications with our attorneys may be protected by attorney-client privilege. However, general 
                information provided on our website or in marketing materials does not create an attorney-client 
                relationship and should not be considered legal advice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Confidentiality</h2>
              <p className="text-gray-700 leading-relaxed">
                We maintain strict confidentiality regarding your trademark applications and business information. 
                However, please note that trademark applications filed with the USPTO become public records and are 
                published in the Official Gazette as part of the registration process.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                We will not disclose your confidential information to third parties except as necessary to provide our 
                services, comply with legal obligations, or with your explicit consent.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                Either party may terminate our services agreement upon written notice. Upon termination, you remain 
                responsible for all fees for services performed up to the termination date. We will provide you with 
                copies of all work product and documents in our possession related to your trademark matters.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                We reserve the right to refuse service or terminate our relationship with any client for any lawful 
                reason, including non-payment, provision of false information, or violation of these terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Dispute Resolution and Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms and Conditions shall be governed by and construed in accordance with the laws of the State 
                of California, without regard to its conflict of law provisions.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Any disputes arising from these terms or our services shall first be attempted to be resolved through 
                good faith negotiation. If negotiation fails, disputes shall be resolved through binding arbitration 
                in Los Angeles County, California, in accordance with the rules of the American Arbitration Association.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Trademark Organization Name, its officers, employees, 
                and agents from any claims, damages, losses, or expenses (including reasonable attorney fees) arising 
                from your use of our services, your trademark activities, your violation of these terms, or your 
                violation of any third-party rights.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Entire Agreement</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms and Conditions, together with our Privacy Policy and any service-specific agreements, 
                constitute the entire agreement between you and Trademark Organization Name regarding our services and 
                supersede all prior agreements, understandings, and communications.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Severability</h2>
              <p className="text-gray-700 leading-relaxed">
                If any provision of these Terms and Conditions is found to be invalid, illegal, or unenforceable, the 
                remaining provisions shall continue in full force and effect. The invalid provision shall be modified 
                to the minimum extent necessary to make it valid and enforceable.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Waiver</h2>
              <p className="text-gray-700 leading-relaxed">
                Our failure to enforce any provision of these terms shall not constitute a waiver of that provision or 
                any other provision. No waiver shall be effective unless made in writing and signed by an authorized 
                representative of Trademark Organization Name.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions or concerns regarding these Terms and Conditions, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700"><strong>Trademark Organization Name</strong></p>
                <p className="text-gray-700">7080 Hollywood Blvd Suite 700</p>
                <p className="text-gray-700">Los Angeles, CA 90028</p>
                <p className="text-gray-700 mt-2">Phone: +1 (123) 123-4567</p>
                <p className="text-gray-700">Email: info@trademarkorganizationname.com</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Acknowledgment</h2>
              <p className="text-gray-700 leading-relaxed">
                By using our website and services, you acknowledge that you have read, understood, and agree to be 
                bound by these Terms and Conditions. If you do not agree to these terms, please discontinue use of 
                our services immediately.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
